import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { CollisionDetection } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useUIStore } from '@/stores/ui/ui-store';

const SIDEBAR_PREFIX = 'sidebar:';

export const stripSidebarPrefix = (id: string): string =>
  id.startsWith(SIDEBAR_PREFIX) ? id.slice(SIDEBAR_PREFIX.length) : id;

export const addSidebarPrefix = (id: string): string =>
  `${SIDEBAR_PREFIX}${id}`;

type DragSource = 'sidebar' | 'table' | null;

export type CrossDndState = {
  activeDragId: string | null;
  activeDragLabel: string | null;
  activeDragSource: DragSource;
  sidebarOverColumnId: string | null;
  /** Whether the pointer is in the top half of the hovered sidebar item (insert before vs after) */
  sidebarInsertBefore: boolean;
  tableOverColumnId: string | null;
};

type ColumnConfig = { id: string; label: string; visible: boolean; groupId?: string };
type ColumnPinSide = 'left' | 'right' | null;

/** Store interface that the provider needs — matches both availability and trades column stores. */
export interface CrossDndColumnStore {
  columns: ColumnConfig[];
  pinnedLeft: string[];
  pinnedRight: string[];
  setColumns: (cols: ColumnConfig[]) => void;
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  toggleColumnVisibility: (columnId: string) => void;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  getPinState: (columnId: string) => ColumnPinSide;
  reorderColumnGroup?: (groupId: string, toIndex: number) => void;
  reorderL2cpColumns?: (fromId: string, toId: string) => void;
}

/** Optional config for L2CP (Lent-to-Counterparties) grouped column support. */
export interface L2cpConfig {
  groupId: string;
  columnIds: readonly string[];
  sidebarPrefix: string;
  tablePrefix: string;
}

export interface CreateCrossDndProviderConfig {
  /** Zustand hook for the column store */
  useColumnStore: {
    <T>(selector: (state: CrossDndColumnStore) => T): T;
    getState: () => CrossDndColumnStore;
  };
  /** DOM selector for the table scroll container, e.g. '[data-trades-table-scroll-container]' */
  tableScrollSelector: string;
  /** Display name for React DevTools */
  displayName: string;
  /** Enable keyboard sensor (PointerSensor is always enabled) */
  enableKeyboardSensor?: boolean;
  /** L2CP grouped column support (inventory only) */
  l2cpConfig?: L2cpConfig;
}

// eslint-disable-next-line react-refresh/only-export-components -- internal helper component used by the factory below; factory pattern is intentional
const ColumnDragGhost: React.FC<{ label: string }> = ({ label }) => (
  <div className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-lg ring-1 ring-gray-200 opacity-90">
    {label}
  </div>
);

export function createCrossDndProvider(config: CreateCrossDndProviderConfig) {
  const { useColumnStore, tableScrollSelector, displayName, enableKeyboardSensor, l2cpConfig } = config;

  const DndStateContext = createContext<CrossDndState>({
    activeDragId: null,
    activeDragLabel: null,
    activeDragSource: null,
    sidebarOverColumnId: null,
    sidebarInsertBefore: true,
    tableOverColumnId: null,
  });

  const useCrossDnd = () => useContext(DndStateContext);

  type ProviderProps = {
    children: React.ReactNode;
    enabled?: boolean;
  };

  const Provider: React.FC<ProviderProps> = ({ children, enabled = false }) => {
    const [activeDragId, setActiveDragId] = useState<string | null>(null);
    const [activeDragLabel, setActiveDragLabel] = useState<string | null>(null);
    const [activeDragSource, setActiveDragSource] = useState<DragSource>(null);
    const [sidebarOverColumnId, setSidebarOverColumnId] = useState<string | null>(null);
    const [sidebarInsertBefore, setSidebarInsertBefore] = useState(true);
    const sidebarInsertBeforeRef = useRef(true);
    const [tableOverColumnId, setTableOverColumnId] = useState<string | null>(null);

    const columns = useColumnStore((s) => s.columns);
    const reorderColumns = useColumnStore((s) => s.reorderColumns);
    const toggleColumnVisibility = useColumnStore((s) => s.toggleColumnVisibility);
    const pinColumn = useColumnStore((s) => s.pinColumn);
    const pinnedLeft = useColumnStore((s) => s.pinnedLeft);
    const pinnedRight = useColumnStore((s) => s.pinnedRight);

    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const pinnedLeftRef = useRef(pinnedLeft);
    pinnedLeftRef.current = pinnedLeft;
    const pinnedRightRef = useRef(pinnedRight);
    pinnedRightRef.current = pinnedRight;

    const dragSourceRef = useRef<DragSource>(null);
    const pointerXRef = useRef(0);
    const overIsPinnedRef = useRef(false);

    // Timer for post-drop sidebar highlight
    const dropHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const highlightDroppedColumn = useCallback((columnId: string) => {
      if (dropHighlightTimerRef.current) clearTimeout(dropHighlightTimerRef.current);
      useUIStore.getState().setSidebarHighlightColumnId(columnId);
      dropHighlightTimerRef.current = setTimeout(() => {
        useUIStore.getState().setSidebarHighlightColumnId(null);
        dropHighlightTimerRef.current = null;
      }, 2000);
    }, []);

    useEffect(() => {
      return () => {
        if (dropHighlightTimerRef.current) {
          clearTimeout(dropHighlightTimerRef.current);
          useUIStore.getState().setSidebarHighlightColumnId(null);
        }
      };
    }, []);

    // Forbidden cursor during table drags outside table
    useEffect(() => {
      if (activeDragSource !== 'table') return;
      const tableContainer = document.querySelector(tableScrollSelector);
      const handler = (e: PointerEvent) => {
        pointerXRef.current = e.clientX;
        if (tableContainer) {
          const rect = tableContainer.getBoundingClientRect();
          const isOverTable = e.clientX >= rect.left && e.clientX <= rect.right;
          if (isOverTable) {
            document.body.classList.remove('dnd-forbidden-cursor');
          } else {
            document.body.classList.add('dnd-forbidden-cursor');
          }
        }
      };
      window.addEventListener('pointermove', handler);
      return () => {
        window.removeEventListener('pointermove', handler);
        document.body.classList.remove('dnd-forbidden-cursor');
      };
    }, [activeDragSource]);

    // Track pointer Y during sidebar drags to compute insert-before vs insert-after
    useEffect(() => {
      if (activeDragSource !== 'sidebar' || !sidebarOverColumnId) {
        return;
      }
      const row = document.querySelector(
        `[data-sortable-id="${CSS.escape(sidebarOverColumnId)}"]`
      ) as HTMLElement | null;
      const handler = (e: PointerEvent) => {
        if (!row) return;
        const rect = row.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;
        const before = e.clientY < midpoint;
        if (before !== sidebarInsertBeforeRef.current) {
          sidebarInsertBeforeRef.current = before;
          setSidebarInsertBefore(before);
        }
      };
      window.addEventListener('pointermove', handler);
      return () => window.removeEventListener('pointermove', handler);
    }, [activeDragSource, sidebarOverColumnId]);

    const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
    const keyboardSensor = useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates });
    const sensors = useSensors(
      ...(enableKeyboardSensor ? [pointerSensor, keyboardSensor] : [pointerSensor])
    );

    const columnAwarePointerWithin = useCallback<CollisionDetection>((args) => {
      const { droppableRects, droppableContainers, pointerCoordinates } = args;
      if (!pointerCoordinates) return [];

      const activeId = String(args.active.id);
      const isSidebarDrag = activeId.startsWith(SIDEBAR_PREFIX)
        || (l2cpConfig != null && activeId.startsWith(l2cpConfig.sidebarPrefix));

      const sidebarCollisions: { id: string; data: { droppableContainer: unknown; value: number } }[] = [];
      const tableCollisions: { id: string; data: { droppableContainer: unknown; value: number }; isPinned: boolean }[] = [];

      for (const container of droppableContainers) {
        const id = String(container.id);
        if (id === activeId) continue;
        const rect = droppableRects.get(container.id);
        if (!rect) continue;

        const isSidebarTarget = id.startsWith(SIDEBAR_PREFIX)
          || (l2cpConfig != null && id.startsWith(l2cpConfig.sidebarPrefix));

        if (isSidebarTarget) {
          if (
            pointerCoordinates.x >= rect.left &&
            pointerCoordinates.x <= rect.left + rect.width &&
            pointerCoordinates.y >= rect.top &&
            pointerCoordinates.y <= rect.top + rect.height
          ) {
            sidebarCollisions.push({ id, data: { droppableContainer: container, value: 0 } });
          }
        } else {
          const columnId = id;
          const isPinned =
            pinnedLeftRef.current.includes(columnId) || pinnedRightRef.current.includes(columnId);
          const liveRect = isPinned && container.node.current
            ? container.node.current.getBoundingClientRect()
            : null;
          const effectiveLeft = liveRect ? liveRect.left : rect.left;
          const effectiveWidth = liveRect ? liveRect.width : rect.width;
          const effectiveTop = liveRect ? liveRect.top : rect.top;

          if (
            pointerCoordinates.x >= effectiveLeft &&
            pointerCoordinates.x <= effectiveLeft + effectiveWidth &&
            pointerCoordinates.y >= effectiveTop
          ) {
            tableCollisions.push({ id, data: { droppableContainer: container, value: 0 }, isPinned });
          }
        }
      }

      const hasPinnedCollision = tableCollisions.some((c) => c.isPinned);
      const resolvedTableCollisions = hasPinnedCollision
        ? tableCollisions.filter((c) => c.isPinned)
        : tableCollisions;

      overIsPinnedRef.current = hasPinnedCollision;

      if (isSidebarDrag) {
        if (sidebarCollisions.length > 0) return sidebarCollisions;
        return resolvedTableCollisions;
      }

      return resolvedTableCollisions;
    }, []);

    const autoScrollConfig = useMemo(() => ({
      canScroll: (element: Element) => {
        if (dragSourceRef.current !== 'table') return true;
        if (overIsPinnedRef.current) return false;
        const rect = element.getBoundingClientRect();
        return pointerXRef.current >= rect.left && pointerXRef.current <= rect.right;
      },
    }), []);

    const handleDragStart = useCallback((event: DragStartEvent) => {
      const rawId = String(event.active.id);

      let columnId: string;
      let isSidebar: boolean;
      if (l2cpConfig) {
        const isSidebarL2cp = rawId.startsWith(l2cpConfig.sidebarPrefix);
        isSidebar = rawId.startsWith(SIDEBAR_PREFIX) || isSidebarL2cp;
        const isL2cp = rawId.startsWith(l2cpConfig.tablePrefix);
        columnId = isSidebarL2cp
          ? rawId.slice(l2cpConfig.sidebarPrefix.length)
          : isSidebar
            ? stripSidebarPrefix(rawId)
            : isL2cp
              ? rawId.slice(l2cpConfig.tablePrefix.length)
              : rawId;
      } else {
        isSidebar = rawId.startsWith(SIDEBAR_PREFIX);
        columnId = isSidebar ? stripSidebarPrefix(rawId) : rawId;
      }

      const col = columnsRef.current.find((c) => c.id === columnId);
      const source: DragSource = isSidebar ? 'sidebar' : 'table';
      setActiveDragId(rawId);
      setActiveDragLabel(col?.label ?? columnId);
      setActiveDragSource(source);
      dragSourceRef.current = source;
    }, []);

    // Indicator-only: just track the hover target, no live reorder.
    const handleDragOver = useCallback((event: DragOverEvent) => {
      const { active, over } = event;
      const activeRaw = String(active.id);
      const isSidebarDrag = activeRaw.startsWith(SIDEBAR_PREFIX);

      if (!isSidebarDrag) {
        setSidebarOverColumnId(null);
        setTableOverColumnId(over ? String(over.id) : null);
        return;
      }

      if (!over) {
        setSidebarOverColumnId(null);
        setTableOverColumnId(null);
        return;
      }

      const overRaw = String(over.id);
      if (overRaw.startsWith(SIDEBAR_PREFIX)) {
        setSidebarOverColumnId(overRaw);
        setTableOverColumnId(null);
      } else {
        setTableOverColumnId(overRaw);
        setSidebarOverColumnId(null);
      }
    }, []);

    const resetDragState = useCallback(() => {
      setActiveDragId(null);
      setActiveDragLabel(null);
      setActiveDragSource(null);
      setSidebarOverColumnId(null);
      setSidebarInsertBefore(true);
      sidebarInsertBeforeRef.current = true;
      setTableOverColumnId(null);
      dragSourceRef.current = null;
      document.body.classList.remove('dnd-forbidden-cursor');
    }, []);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
      const { active, over } = event;
      const insertBefore = sidebarInsertBeforeRef.current;
      resetDragState();

      if (!over || active.id === over.id) return;

      const activeRaw = String(active.id);
      const overRaw = String(over.id);

      // ── L2CP inner column reorder (sidebar or table) ──
      if (l2cpConfig) {
        const { sidebarPrefix: l2cpSidebarPrefix, tablePrefix: l2cpTablePrefix, groupId, columnIds } = l2cpConfig;
        const reorderL2cpColumns = useColumnStore.getState().reorderL2cpColumns;
        const reorderColumnGroup = useColumnStore.getState().reorderColumnGroup;

        if (activeRaw.startsWith(l2cpSidebarPrefix) && overRaw.startsWith(l2cpSidebarPrefix)) {
          const fromId = activeRaw.slice(l2cpSidebarPrefix.length);
          const toId = overRaw.slice(l2cpSidebarPrefix.length);
          reorderL2cpColumns?.(fromId, toId);
          highlightDroppedColumn(fromId);
          return;
        }

        if (activeRaw.startsWith(l2cpTablePrefix) && overRaw.startsWith(l2cpTablePrefix)) {
          const fromId = activeRaw.slice(l2cpTablePrefix.length);
          const toId = overRaw.slice(l2cpTablePrefix.length);
          reorderL2cpColumns?.(fromId, toId);
          highlightDroppedColumn(fromId);
          return;
        }

        // L2CP group drag and inner column protection (sidebar → sidebar only)
        const isSidebarActive = activeRaw.startsWith(SIDEBAR_PREFIX);
        const isSidebarOver = overRaw.startsWith(SIDEBAR_PREFIX);
        if (isSidebarActive && isSidebarOver) {
          const activeId = stripSidebarPrefix(activeRaw);
          const overId = stripSidebarPrefix(overRaw);
          const cols = columnsRef.current;

          if (activeId === groupId) {
            const overIndex = cols.findIndex((col) => col.id === overId);
            const groupStart = cols.findIndex((c) => c.groupId === groupId);
            if (groupStart === -1) return;
            const groupEnd = groupStart + columnIds.length;
            if (overIndex >= groupStart && overIndex < groupEnd) return;
            reorderColumnGroup?.(groupId, overIndex);
            highlightDroppedColumn(activeId);
            return;
          }
          const activeCol = cols.find((col) => col.id === activeId);
          if (activeCol?.groupId === groupId) return;
        }
      }

      const isSidebarActive = activeRaw.startsWith(SIDEBAR_PREFIX);
      const isSidebarOver = overRaw.startsWith(SIDEBAR_PREFIX);

      let droppedColumnId: string | null = null;

      if (isSidebarActive && isSidebarOver) {
        // Sidebar → Sidebar reorder
        const activeId = stripSidebarPrefix(activeRaw);
        const overId = stripSidebarPrefix(overRaw);

        const { pinnedLeft, pinnedRight } = useColumnStore.getState();
        const overIsInLeft = pinnedLeft.includes(overId);
        const overIsInRight = pinnedRight.includes(overId);
        const activePinState = useColumnStore.getState().getPinState(activeId);

        if (overIsInLeft && activePinState !== 'left') {
          pinColumn(activeId, 'left');
        } else if (overIsInRight && activePinState !== 'right') {
          pinColumn(activeId, 'right');
        } else if (!overIsInLeft && !overIsInRight && activePinState !== null) {
          pinColumn(activeId, null);
        }

        const freshCols = useColumnStore.getState().columns;
        const oldIndex = freshCols.findIndex((col) => col.id === activeId);
        const overIndex = freshCols.findIndex((col) => col.id === overId);

        let adjustedIndex = overIndex;
        if (insertBefore && oldIndex < overIndex) {
          adjustedIndex = overIndex - 1;
        } else if (!insertBefore && oldIndex > overIndex) {
          adjustedIndex = overIndex + 1;
        }
        reorderColumns(oldIndex, adjustedIndex);
        droppedColumnId = activeId;
      } else if (isSidebarActive && !isSidebarOver) {
        // Sidebar → Table drop
        const draggedColumnId = stripSidebarPrefix(activeRaw);
        const targetColumnId = overRaw;
        const draggedCol = columnsRef.current.find((c) => c.id === draggedColumnId);

        if (!draggedCol) return;

        if (!draggedCol.visible) {
          toggleColumnVisibility(draggedColumnId);
        }

        // Auto-pin/unpin BEFORE reorder so column lands in the correct zone
        const { pinnedLeft: curPinnedLeft, pinnedRight: curPinnedRight } = useColumnStore.getState();
        const targetPinSide = curPinnedLeft.includes(targetColumnId) ? 'left'
          : curPinnedRight.includes(targetColumnId) ? 'right'
          : null;
        const draggedPinState = useColumnStore.getState().getPinState(draggedColumnId);
        if (targetPinSide !== draggedPinState) {
          pinColumn(draggedColumnId, targetPinSide);
        }

        const updatedCols = useColumnStore.getState().columns;
        const fromStoreIdx = updatedCols.findIndex((c) => c.id === draggedColumnId);
        const toStoreIdx = updatedCols.findIndex((c) => c.id === targetColumnId);
        if (fromStoreIdx !== -1 && toStoreIdx !== -1) {
          reorderColumns(fromStoreIdx, toStoreIdx);
        }
        droppedColumnId = draggedColumnId;
      } else if (!isSidebarActive && !isSidebarOver) {
        // Table → Table: unified same-zone and cross-zone
        const activeId = activeRaw;
        const targetId = overRaw;
        const activePinState = useColumnStore.getState().getPinState(activeId);
        const { pinnedLeft, pinnedRight } = useColumnStore.getState();
        const targetPinZone = pinnedLeft.includes(targetId) ? 'left'
          : pinnedRight.includes(targetId) ? 'right'
          : null;

        if (activePinState !== targetPinZone) {
          pinColumn(activeId, targetPinZone);
        }

        const freshCols = useColumnStore.getState().columns;
        const fromIdx = freshCols.findIndex((c) => c.id === activeId);
        const toIdx = freshCols.findIndex((c) => c.id === targetId);
        if (fromIdx !== -1 && toIdx !== -1 && fromIdx !== toIdx) {
          reorderColumns(fromIdx, toIdx);
        }
        droppedColumnId = activeId;
      }
      // Table → Sidebar: ignored

      if (droppedColumnId) {
        highlightDroppedColumn(droppedColumnId);
      }
    }, [resetDragState, reorderColumns, toggleColumnVisibility, pinColumn, highlightDroppedColumn]);

    const handleDragCancel = useCallback(() => {
      resetDragState();
    }, [resetDragState]);

    const contextValue = useMemo<CrossDndState>(
      () => ({ activeDragId, activeDragLabel, activeDragSource, sidebarOverColumnId, sidebarInsertBefore, tableOverColumnId }),
      [activeDragId, activeDragLabel, activeDragSource, sidebarOverColumnId, sidebarInsertBefore, tableOverColumnId]
    );

    if (!enabled) {
      return (
        <DndStateContext.Provider value={contextValue}>
          {children}
        </DndStateContext.Provider>
      );
    }

    return (
      <DndStateContext.Provider value={contextValue}>
        <DndContext
          sensors={sensors}
          collisionDetection={columnAwarePointerWithin}
          autoScroll={autoScrollConfig}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {children}
          <DragOverlay dropAnimation={null} zIndex={9999}>
            {activeDragId && activeDragLabel ? (
              <ColumnDragGhost label={activeDragLabel} />
            ) : null}
          </DragOverlay>
        </DndContext>
      </DndStateContext.Provider>
    );
  };

  Provider.displayName = displayName;

  return { Provider, useCrossDnd };
}
